import { createFileRoute } from "@tanstack/react-router";
import { buildAppMeta, SITE_URL } from "@/lib/seo";
import { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";

type Search = { token?: string };

export const Route = createFileRoute("/unsubscribe")({
  head: () => ({
    meta: buildAppMeta({
      title: "Unsubscribe — CoachQuick",
      description: "Manage your email preferences and unsubscribe from CoachQuick notifications.",
      path: "/unsubscribe",
    }),
    links: [{ rel: "canonical", href: `${SITE_URL}/unsubscribe` }],
  }),
  validateSearch: (s: Record<string, unknown>): Search => ({
    token: (s.token as string) || undefined,
  }),
  component: UnsubscribePage,
});

function UnsubscribePage() {
  const { token } = Route.useSearch();
  const [state, setState] = useState<"loading" | "valid" | "already" | "invalid" | "done" | "error">("loading");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!token) {
      setState("invalid");
      return;
    }
    fetch(`/email/unsubscribe?token=${encodeURIComponent(token)}`)
      .then(async (r) => {
        const data = await r.json().catch(() => ({}));
        if (!r.ok) return setState("invalid");
        if (data.valid) return setState("valid");
        if (data.reason === "already_unsubscribed") return setState("already");
        setState("invalid");
      })
      .catch(() => setState("error"));
  }, [token]);

  async function confirm() {
    if (!token) return;
    setBusy(true);
    try {
      const r = await fetch("/email/unsubscribe", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token }),
      });
      const data = await r.json().catch(() => ({}));
      if (data.success) setState("done");
      else if (data.reason === "already_unsubscribed") setState("already");
      else setState("error");
    } catch {
      setState("error");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-secondary/30 px-4">
      <Card className="w-full max-w-md p-8">
        <h1 className="text-xl font-bold tracking-tight">Unsubscribe</h1>
        <div className="mt-4 text-sm text-muted-foreground">
          {state === "loading" && "Checking your link…"}
          {state === "valid" && "Click below to stop receiving emails from CoachQuick."}
          {state === "already" && "You're already unsubscribed. No further emails will be sent."}
          {state === "done" && "You've been unsubscribed. Sorry to see you go!"}
          {state === "invalid" && "This unsubscribe link is invalid or expired."}
          {state === "error" && "Something went wrong. Please try again later."}
        </div>
        {state === "valid" && (
          <Button onClick={confirm} disabled={busy} className="mt-6 w-full">
            {busy ? "Unsubscribing…" : "Confirm unsubscribe"}
          </Button>
        )}
      </Card>
    </div>
  );
}
